import { ChangeDetectionStrategy, Component, Inject, Optional } from '@angular/core';
import { AbstractControl, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { UserReportsApiService } from '@digilize/shared/data/services/src';
import { AlertType, ApiError, AppConfig, UserReportAddModel } from '@digilize/shared/definitions/src';
import { APP_CONFIG } from '@digilize/shared/utils/tokens/src';

import { AlertService } from '../alert.service';

export interface ReportErrorDialogData {
  requestId: string;
  errorType: string | null;
  errorKind: string | null;
  /** The message as the alert bar showed it, in the user's language. */
  errorMessage: string | null;
}

// The backend's limits on a report's description.
const DESCRIPTION_MIN_LENGTH = 60;
const DESCRIPTION_MAX_LENGTH = 2000;

const minTrimmedLength =
  (min: number) =>
  (control: AbstractControl): ValidationErrors | null =>
    (control.value ?? '').trim().length < min ? { minTrimmedLength: { min } } : null;

@Component({
  selector: 'lib-report-error-dialog',
  templateUrl: './report-error-dialog.component.html',
  styleUrls: ['./report-error-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ReportErrorDialogComponent {
  readonly DESCRIPTION_MIN_LENGTH = DESCRIPTION_MIN_LENGTH;
  readonly DESCRIPTION_MAX_LENGTH = DESCRIPTION_MAX_LENGTH;

  form = new UntypedFormGroup({
    description: new UntypedFormControl('', [
      Validators.required,
      Validators.maxLength(DESCRIPTION_MAX_LENGTH),
      minTrimmedLength(DESCRIPTION_MIN_LENGTH),
    ]),
  });
  saving = false;

  constructor(
    public dialogRef: MatDialogRef<ReportErrorDialogComponent, boolean>,
    @Inject(MAT_DIALOG_DATA) public data: ReportErrorDialogData,
    private userReportsApiService: UserReportsApiService,
    private alertService: AlertService,
    private router: Router,
    @Optional() @Inject(APP_CONFIG) private appConfig: AppConfig | null
  ) {}

  get description(): AbstractControl {
    return this.form.get('description');
  }

  get trimmedLength(): number {
    return (this.description.value ?? '').trim().length;
  }

  get canSubmit(): boolean {
    return !this.saving && this.form.valid;
  }

  submit(): void {
    if (!this.canSubmit) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;
    this.userReportsApiService.addReport(this.report()).subscribe({
      next: () => {
        this.saving = false;
        this.dialogRef.close(true);
        this.alertService.setSimpleAlert(AlertType.Success, 'alert.report.title', 'alert.report.success', true);
      },
      error: (err) => {
        this.saving = false;
        const apiError: ApiError | undefined = err?.error?.error;

        if (apiError?.info?.field === 'description') {
          this.description.setErrors({ api: { kind: apiError.info.kind, message: apiError.info.message } });
          this.description.markAsTouched();
          return;
        }

        this.alertService.handleApiError(apiError);
      },
    });
  }

  close(): void {
    if (!this.saving) {
      this.dialogRef.close(false);
    }
  }

  private report(): UserReportAddModel {
    return {
      request_id: this.data.requestId,
      description: this.description.value.trim(),
      context: this.router.url,
      error_type: this.data.errorType ?? null,
      error_kind: this.data.errorKind ?? null,
      error_message: this.data.errorMessage ?? null,
      app_version: this.appConfig?.version ?? null,
    };
  }
}
